import React, { useState } from "react";
import { connect } from "react-redux";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import InputAdornment from "@mui/material/InputAdornment";

import { StoreState } from "../redux/configure-store";
import Savebar from "../common/components/cancel-save";
import { updateUserSettings } from "./user-actions";
import {
  navigateToDashboardPage,
  navigateToPasswordPage,
} from "../routing/route-actions";
import { backgrounds } from "../theme/theme";

const SettingsPage = (props: any) => {
  const { user, navigateToDashboardPage, navigateToPasswordPage } = props;

  const [state, setState] = useState({
    userId: user.id,
    firstName: user.firstName || "",
    lastName: user.lastName || "",
    emailAddress: user.emailAddress || "",
  });

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setState((prevState) => ({
      ...prevState,
      [e.target.name]: e.target.value,
    }));
  };

  const onSave = () => {
    props.updateUserSettings(state.userId);
  };

  const onCancel = () => {
    navigateToDashboardPage();
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: backgrounds.page,
        py: 4,
        px: { xs: 2, md: 3 },
      }}
    >
      <Box sx={{ maxWidth: 900, mx: "auto" }}>
        {/* Page Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" fontWeight={600} gutterBottom>
            User Settings
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your account settings and preferences
          </Typography>
        </Box>

        {/* Profile Section */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            mb: 3,
          }}
        >
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Profile Information
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Your basic account information
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="First Name"
                placeholder="First Name"
                name="firstName"
                value={state.firstName}
                onChange={onChange}
                disabled
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Last Name"
                placeholder="Last Name"
                name="lastName"
                value={state.lastName}
                onChange={onChange}
                disabled
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email Address"
                placeholder="Email Address"
                name="emailAddress"
                value={state.emailAddress}
                onChange={onChange}
                disabled
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Security Section */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            mb: 3,
          }}
        >
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Security
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Manage your password and security settings
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <LockIcon color="action" />
            <Box sx={{ flex: 1 }}>
              <Typography variant="body1" fontWeight={500}>
                Password
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Change your account password
              </Typography>
            </Box>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => navigateToPasswordPage()}
            >
              Change Password
            </Button>
          </Box>
        </Paper>

        {/* Action Buttons */}
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Savebar disableSave={false} onSave={onSave} onCancel={onCancel} />
        </Box>
      </Box>
    </Box>
  );
};

const mapStateToProps = (storeState: StoreState) => ({
  user: storeState.authentication.user,
  userSettings: storeState.user.userSettings,
});

export default connect(mapStateToProps, {
  navigateToDashboardPage,
  navigateToPasswordPage,
  updateUserSettings,
})(SettingsPage);
